import { h, uid, formatWhen } from './core.js';

/*
 * Review: comments and track changes for Document.
 *
 * Comments are anchored to a text range wrapped in
 *   <span class="dfc-comment" data-comment-id="…">
 * and the thread itself ({ id, text, author, at, resolved, replies }) lives in
 * the Review controller, so a document is { html, comments }.
 *
 * Track changes records typing as <ins class="dfc-ins"> and deletions as
 * <del class="dfc-del"> carrying data-change-id / data-author / data-at, using
 * the beforeinput event so the browser never mutates the DOM on its own while
 * tracking is on. Accepting or rejecting unwraps or removes those elements.
 */

/** Ids must stay unique across sessions, since stored documents are reopened with their existing marks. */
const rid = (prefix) => uid(prefix) + '-' + Math.random().toString(36).slice(2, 8);
const COMMENT_SEL = 'span.dfc-comment';
const CHANGE_SEL = 'ins.dfc-ins, del.dfc-del';

export class Review {
	constructor(doc) {
		this.doc = doc;
		this.comments = (doc.options.comments || []).map((c) => Object.assign({ replies: [], resolved: false }, c));
		this.tracking = !!doc.options.trackChanges;
		this.activeId = null;
		this.panelOpen = !!doc.options.showReview;
	}

	get user() {
		const u = this.doc.options.user;
		if (u && (u.name || u.id)) return { id: u.id === undefined ? null : u.id, name: u.name || String(u.id) };
		return { id: null, name: (this.doc.options.meta && this.doc.options.meta.author) || 'Anonymous' };
	}
	get editable() { return !(this.doc.options.readOnly || this.doc.options.disabled); }
	_stamp(el) { el.setAttribute('data-author', this.user.name); el.setAttribute('data-at', new Date().toISOString()); }

	// ------------------------------------------------------------ mount
	install() {
		const ed = this.doc.editor;
		this._onBeforeInput = (e) => this._beforeInput(e);
		this._onClick = (e) => {
			const c = e.target.closest && e.target.closest(COMMENT_SEL);
			if (c && ed.contains(c)) this.setActive(c.getAttribute('data-comment-id'));
		};
		ed.addEventListener('beforeinput', this._onBeforeInput);
		ed.addEventListener('click', this._onClick);
		this.panel = h('aside', { className: 'dfc-document-review', hidden: !this.panelOpen, 'aria-label': 'Review' });
		this.renderPanel();
		this._syncButtons();
	}
	uninstall() {
		const ed = this.doc.editor;
		if (ed) { ed.removeEventListener('beforeinput', this._onBeforeInput); ed.removeEventListener('click', this._onClick); }
	}
	_syncButtons() {
		const b = this.doc.buttons || {};
		if (b.track) b.track.classList.toggle('is-active', this.tracking);
		if (b.review) b.review.classList.toggle('is-active', this.panelOpen);
	}

	// ------------------------------------------------------------ track changes
	setTracking(on) {
		this.tracking = !!on;
		this._syncButtons();
		this.doc.emit('trackChanged', { tracking: this.tracking });
		return this;
	}
	togglePanel(on) {
		this.panelOpen = on === undefined ? !this.panelOpen : !!on;
		if (this.panel) this.panel.hidden = !this.panelOpen;
		this._syncButtons();
		return this;
	}

	_beforeInput(e) {
		if (!this.tracking || !this.editable) return;
		const t = e.inputType || '';
		const sel = window.getSelection();
		if (!sel || !sel.rangeCount) return;
		if (t === 'insertText' || t === 'insertFromPaste' || t === 'insertFromDrop' || t === 'insertLineBreak' || t === 'insertReplacementText') {
			let text = e.data;
			if (t === 'insertLineBreak') text = '\n';
			if ((text === null || text === undefined) && e.dataTransfer) text = e.dataTransfer.getData('text/plain');
			if (!text) return;
			e.preventDefault();
			let range = sel.getRangeAt(0);
			if (!range.collapsed) range = this._markDeleted(range, false);
			this._insertTracked(range, text);
			this.doc._afterEdit('user');
			return;
		}
		if (t.indexOf('delete') === 0) {
			e.preventDefault();
			let range = sel.getRangeAt(0);
			if (range.collapsed) {
				const tr = e.getTargetRanges && e.getTargetRanges()[0];
				if (!tr) return;
				range = document.createRange(); range.setStart(tr.startContainer, tr.startOffset); range.setEnd(tr.endContainer, tr.endOffset);
				if (range.collapsed) return;
			}
			this._markDeleted(range, t.indexOf('Backward') !== -1);
			this.doc._afterEdit('user');
		}
		// insertParagraph and formatting commands run untracked.
	}

	_ownIns(node) {
		let n = node && node.nodeType === 3 ? node.parentNode : node;
		const ins = n && n.closest && n.closest('ins.dfc-ins');
		return ins && this.doc.editor.contains(ins) && ins.getAttribute('data-author') === this.user.name && !ins.closest('del.dfc-del') ? ins : null;
	}
	_setCaret(node, offset) {
		const r = document.createRange(); r.setStart(node, offset); r.collapse(true);
		const s = window.getSelection(); s.removeAllRanges(); s.addRange(r);
		this.doc._saveSelection();
	}
	_caretAfter(el) { const p = el.parentNode; this._setCaret(p, [...p.childNodes].indexOf(el) + 1); }
	_caretBefore(el) { const p = el.parentNode; this._setCaret(p, [...p.childNodes].indexOf(el)); }

	_insertTracked(range, text) {
		const own = this._ownIns(range.startContainer);
		const textNode = document.createTextNode(text);
		if (own) {
			range.insertNode(textNode);
			this._setCaret(textNode, textNode.length);
			return;
		}
		const ins = h('ins', { className: 'dfc-ins', dataset: { changeId: rid('chg') } }, textNode);
		this._stamp(ins);
		range.insertNode(ins);
		this._setCaret(textNode, textNode.length);
	}

	/** Wraps the range in a tracked deletion (or really deletes own untracked insertions). Returns a collapsed range at the caret. */
	_markDeleted(range, backward) {
		const own = this._ownIns(range.startContainer);
		const ownEnd = this._ownIns(range.endContainer);
		if (own && own === ownEnd) {
			range.deleteContents();
			if (!own.textContent) { const p = own.parentNode; const idx = [...p.childNodes].indexOf(own); own.remove(); this._setCaret(p, idx); }
			else this._setCaret(range.startContainer, range.startOffset);
			return window.getSelection().getRangeAt(0);
		}
		const startDel = range.startContainer.nodeType === 3 ? range.startContainer.parentNode.closest('del.dfc-del') : range.startContainer.closest && range.startContainer.closest('del.dfc-del');
		const endDel = range.endContainer.nodeType === 3 ? range.endContainer.parentNode.closest('del.dfc-del') : range.endContainer.closest && range.endContainer.closest('del.dfc-del');
		if (startDel && startDel === endDel && this.doc.editor.contains(startDel)) {
			// Already deleted: step over it instead of deleting twice.
			if (backward) this._caretBefore(startDel); else this._caretAfter(startDel);
			return window.getSelection().getRangeAt(0);
		}
		const frag = range.extractContents();
		// Own insertions inside the range simply disappear; nested deletions flatten into this one.
		frag.querySelectorAll('ins.dfc-ins').forEach((i) => { if (i.getAttribute('data-author') === this.user.name) i.remove(); });
		frag.querySelectorAll('del.dfc-del').forEach((d) => { while (d.firstChild) d.parentNode.insertBefore(d.firstChild, d); d.remove(); });
		if (!frag.textContent && !frag.querySelector('img')) { this._setCaret(range.startContainer, range.startOffset); return window.getSelection().getRangeAt(0); }
		const del = h('del', { className: 'dfc-del', dataset: { changeId: rid('chg') } });
		this._stamp(del);
		del.appendChild(frag);
		range.insertNode(del);
		if (backward) this._caretBefore(del); else this._caretAfter(del);
		return window.getSelection().getRangeAt(0);
	}

	get changes() {
		const seen = new Map();
		[...this.doc.editor.querySelectorAll(CHANGE_SEL)].forEach((el) => {
			const id = el.getAttribute('data-change-id') || rid('chg');
			if (!el.getAttribute('data-change-id')) el.setAttribute('data-change-id', id);
			const existing = seen.get(id);
			const text = el.textContent || (el.querySelector('img') ? '[image]' : '');
			if (existing) { existing.text += text; existing.elements.push(el); return; }
			seen.set(id, { id, type: el.tagName === 'INS' ? 'insert' : 'delete', author: el.getAttribute('data-author') || '', at: el.getAttribute('data-at') || '', text, elements: [el] });
		});
		return [...seen.values()];
	}
	_resolveChange(id, accept) {
		const els = [...this.doc.editor.querySelectorAll(`${CHANGE_SEL}`)].filter((el) => el.getAttribute('data-change-id') === id);
		if (!els.length) return false;
		els.forEach((el) => {
			const keep = el.tagName === 'INS' ? accept : !accept;
			if (keep) { while (el.firstChild) el.parentNode.insertBefore(el.firstChild, el); }
			el.remove();
		});
		return true;
	}
	acceptChange(id) { if (this._resolveChange(id, true)) this._changed(); return this; }
	rejectChange(id) { if (this._resolveChange(id, false)) this._changed(); return this; }
	acceptAll() { this.changes.forEach((c) => this._resolveChange(c.id, true)); this._changed(); return this; }
	rejectAll() { this.changes.forEach((c) => this._resolveChange(c.id, false)); this._changed(); return this; }
	_changed() { this.doc.editor.normalize(); this.doc._afterEdit('user'); }
	focusChange(id) {
		const el = this.doc.editor.querySelector(`[data-change-id="${id}"]`);
		if (!el) return;
		el.scrollIntoView({ block: 'center' });
		el.classList.add('is-flash'); setTimeout(() => el.classList.remove('is-flash'), 1200);
	}

	/** Static: HTML with every change accepted and comment anchors removed. */
	static clean(root) {
		root.querySelectorAll('ins.dfc-ins').forEach((el) => { while (el.firstChild) el.parentNode.insertBefore(el.firstChild, el); el.remove(); });
		root.querySelectorAll('del.dfc-del').forEach((el) => el.remove());
		root.querySelectorAll(COMMENT_SEL).forEach((el) => { while (el.firstChild) el.parentNode.insertBefore(el.firstChild, el); el.remove(); });
		root.querySelectorAll('mark.dfc-find').forEach((el) => { el.replaceWith(document.createTextNode(el.textContent)); });
		root.normalize();
		return root;
	}

	// ------------------------------------------------------------ comments
	get list() { return this.comments.map((c) => Object.assign({}, c, { replies: (c.replies || []).map((r) => Object.assign({}, r)) })); }
	_anchor(id) { return this.doc.editor.querySelector(`${COMMENT_SEL}[data-comment-id="${id}"]`); }

	/** Adds a comment on the current selection. Returns the comment, or null when nothing is selected. */
	add(text) {
		if (!text || !this.editable) return null;
		this.doc._restoreSelection();
		const sel = window.getSelection();
		if (!sel || !sel.rangeCount) return null;
		const range = sel.getRangeAt(0);
		if (range.collapsed || !this.doc.editor.contains(range.commonAncestorContainer)) return null;
		const id = rid('cmt');
		const span = h('span', { className: 'dfc-comment', dataset: { commentId: id } });
		try { range.surroundContents(span); } catch (e) { span.appendChild(range.extractContents()); range.insertNode(span); }
		const c = { id, text: String(text), author: this.user.name, authorId: this.user.id, at: new Date().toISOString(), resolved: false, replies: [] };
		this.comments.push(c);
		this.doc._afterEdit('user');
		this._commentsChanged();
		this.setActive(id);
		return Object.assign({}, c);
	}
	reply(id, text) {
		const c = this.comments.find((x) => x.id === id);
		if (!c || !text) return this;
		c.replies = c.replies || [];
		c.replies.push({ text: String(text), author: this.user.name, authorId: this.user.id, at: new Date().toISOString() });
		this._commentsChanged();
		return this;
	}
	resolve(id, resolved = true) {
		const c = this.comments.find((x) => x.id === id);
		if (!c) return this;
		c.resolved = !!resolved;
		const a = this._anchor(id); if (a) a.classList.toggle('is-resolved', c.resolved);
		this._commentsChanged();
		return this;
	}
	remove(id) {
		this.comments = this.comments.filter((x) => x.id !== id);
		[...this.doc.editor.querySelectorAll(`${COMMENT_SEL}[data-comment-id="${id}"]`)].forEach((el) => { while (el.firstChild) el.parentNode.insertBefore(el.firstChild, el); el.remove(); });
		this.doc.editor.normalize();
		if (this.activeId === id) this.activeId = null;
		this.doc._afterEdit('user');
		this._commentsChanged();
		return this;
	}
	setActive(id) {
		this.activeId = id;
		[...this.doc.editor.querySelectorAll(COMMENT_SEL)].forEach((el) => el.classList.toggle('is-active', el.getAttribute('data-comment-id') === id));
		const a = this._anchor(id); if (a) a.scrollIntoView({ block: 'center' });
		if (!this.panelOpen) this.togglePanel(true);
		this.renderComments();
		const card = this.panel && this.panel.querySelector(`[data-comment-card="${id}"]`);
		if (card) card.scrollIntoView({ block: 'nearest' });
		return this;
	}
	/** Re-applies resolved/active classes after the editor HTML was replaced (undo, value set). */
	syncAnchors() {
		this.comments.forEach((c) => { const a = this._anchor(c.id); if (a) { a.classList.toggle('is-resolved', !!c.resolved); a.classList.toggle('is-active', c.id === this.activeId); } });
	}
	_commentsChanged() { this.renderComments(); this.doc.emit('commentsChanged', { comments: this.list }); }

	// ------------------------------------------------------------ panel
	renderPanel() {
		if (!this.panel) return;
		this.panel.innerHTML = '';
		this.commentsEl = h('section', { className: 'dfc-review-section' });
		this.changesEl = h('section', { className: 'dfc-review-section' });
		this.panel.appendChild(this.commentsEl);
		this.panel.appendChild(this.changesEl);
		this.renderComments();
		this.renderChanges();
	}
	_when(iso) { const w = formatWhen(iso, this.doc.options.locale); return w.date ? `${w.date} ${w.time}` : ''; }
	_btn(label, onClick, ghost) { return h('button', { type: 'button', className: 'dfc-btn dfc-btn-sm' + (ghost ? ' dfc-btn-ghost' : ''), text: label, onMousedown: (e) => e.preventDefault(), onClick }); }

	renderComments() {
		if (!this.commentsEl) return;
		const el = this.commentsEl; el.innerHTML = '';
		const open = this.comments.filter((c) => !c.resolved).length;
		el.appendChild(h('h4', { className: 'dfc-review-title', text: `Comments (${open}${this.comments.length !== open ? ' open of ' + this.comments.length : ''})` }));
		if (!this.comments.length) { el.appendChild(h('p', { className: 'dfc-review-empty', text: this.editable ? 'Select some text and use the comment button to start a thread.' : 'No comments.' })); return; }
		this.comments.forEach((c) => {
			const anchored = !!this._anchor(c.id);
			const card = h('div', { className: 'dfc-review-card' + (c.id === this.activeId ? ' is-active' : '') + (c.resolved ? ' is-resolved' : ''), dataset: { commentCard: c.id }, onClick: (e) => { if (e.target.closest('button, textarea')) return; if (this.activeId !== c.id) this.setActive(c.id); } });
			card.appendChild(h('div', { className: 'dfc-review-meta' }, h('b', { text: c.author || '' }), h('span', { text: this._when(c.at) })));
			card.appendChild(h('div', { className: 'dfc-review-text', text: c.text }));
			if (!anchored) card.appendChild(h('div', { className: 'dfc-review-note', text: 'The commented text was removed.' }));
			(c.replies || []).forEach((r) => {
				card.appendChild(h('div', { className: 'dfc-review-reply' }, h('div', { className: 'dfc-review-meta' }, h('b', { text: r.author || '' }), h('span', { text: this._when(r.at) })), h('div', { className: 'dfc-review-text', text: r.text })));
			});
			if (this.editable) {
				if (c.id === this.activeId && !c.resolved) {
					const ta = h('textarea', { className: 'dfc-review-input', rows: '2', placeholder: 'Reply', 'aria-label': 'Reply' });
					ta.addEventListener('keydown', (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); if (ta.value.trim()) this.reply(c.id, ta.value.trim()); } });
					card.appendChild(ta);
					card.appendChild(h('div', { className: 'dfc-review-actions' }, this._btn('Reply', () => { if (ta.value.trim()) this.reply(c.id, ta.value.trim()); }), this._btn('Resolve', () => this.resolve(c.id, true), true), this._btn('Delete', () => this.remove(c.id), true)));
				} else {
					card.appendChild(h('div', { className: 'dfc-review-actions' }, c.resolved ? this._btn('Reopen', () => this.resolve(c.id, false), true) : null, this._btn('Delete', () => this.remove(c.id), true)));
				}
			}
			el.appendChild(card);
		});
	}

	renderChanges() {
		if (!this.changesEl) return;
		const el = this.changesEl; el.innerHTML = '';
		const changes = this.changes;
		el.appendChild(h('h4', { className: 'dfc-review-title' }, h('span', { text: `Changes (${changes.length})` }), h('span', { className: 'dfc-review-tracking' + (this.tracking ? ' is-on' : ''), text: this.tracking ? 'Tracking on' : 'Tracking off' })));
		if (!changes.length) { el.appendChild(h('p', { className: 'dfc-review-empty', text: this.tracking ? 'Edits are now recorded here.' : 'Turn on track changes to record edits.' })); return; }
		if (this.editable) el.appendChild(h('div', { className: 'dfc-review-actions' }, this._btn('Accept all', () => this.acceptAll()), this._btn('Reject all', () => this.rejectAll(), true)));
		changes.forEach((c) => {
			const snippet = (c.text || '').replace(/\s+/g, ' ').trim();
			const card = h('div', { className: 'dfc-review-card dfc-review-change', dataset: { changeCard: c.id }, onClick: (e) => { if (!e.target.closest('button')) this.focusChange(c.id); } });
			card.appendChild(h('div', { className: 'dfc-review-meta' }, h('span', { className: 'dfc-review-badge is-' + c.type, text: c.type === 'insert' ? 'Inserted' : 'Deleted' }), h('b', { text: c.author }), h('span', { text: this._when(c.at) })));
			card.appendChild(h('div', { className: 'dfc-review-text', text: snippet.length > 120 ? snippet.slice(0, 117) + '…' : snippet || '(formatting)' }));
			if (this.editable) card.appendChild(h('div', { className: 'dfc-review-actions' }, this._btn('Accept', () => this.acceptChange(c.id)), this._btn('Reject', () => this.rejectChange(c.id), true)));
			el.appendChild(card);
		});
	}

	/** Toolbar popover to write a new comment on the current selection. */
	openCommentPopover(anchor) {
		const doc = this.doc;
		doc._restoreSelection();
		const sel = window.getSelection();
		const hasSel = sel && sel.rangeCount && !sel.getRangeAt(0).collapsed && doc.editor.contains(sel.getRangeAt(0).commonAncestorContainer);
		doc.popover.innerHTML = ''; doc.popover.hidden = false; doc.popover.style.left = Math.max(0, anchor.offsetLeft) + 'px';
		if (!hasSel) { doc.popover.appendChild(h('div', { className: 'dfc-richtext-popover-row', text: 'Select the text you want to comment on first.' })); return; }
		const ta = h('textarea', { className: 'dfc-review-input', rows: '3', placeholder: 'Comment', 'aria-label': 'Comment', style: { minWidth: '260px' } });
		const submit = () => { const t = ta.value.trim(); if (!t) return; doc._closePopover(); doc._restoreSelection(); this.add(t); };
		ta.addEventListener('keydown', (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } if (e.key === 'Escape') doc._closePopover(); });
		doc.popover.appendChild(h('div', { className: 'dfc-richtext-popover-row' }, ta));
		doc.popover.appendChild(h('div', { className: 'dfc-richtext-popover-row' }, h('button', { type: 'button', className: 'dfc-btn', text: 'Add comment', onClick: submit }), h('button', { type: 'button', className: 'dfc-btn dfc-btn-ghost', text: 'Cancel', onClick: () => doc._closePopover() })));
		setTimeout(() => ta.focus(), 0);
	}
}
